// src/taskpane/features/integrations/components/dartsip/SearchFilters.tsx

import React, { useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DartsIPSearchParams } from '../../types/integrationTypes';
import { cn } from '@/lib/utils';

interface SearchFiltersProps {
  onSearch: (params: DartsIPSearchParams) => void;
  isSearching: boolean;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({ onSearch, isSearching }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [keywords, setKeywords] = useState<string>('');
  const [selectedProcedures, setSelectedProcedures] = useState<string[]>([]);
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>([]);
  const [selectedLegalPoints, setSelectedLegalPoints] = useState<string[]>([]);
  const [selectedCourts, setSelectedCourts] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const procedures = [
    { value: 'opposition', label: 'Opposition' },
    { value: 'invalidity', label: 'Invalidity' },
    { value: 'revocation', label: 'Revocation / Invalidity' },
    { value: 'infringement', label: 'Infringement' },
  ];

  const jurisdictions = [
    { value: 'EU', label: 'UE (EUIPO / TG / TJUE)' },
    { value: 'ES', label: 'España (OEPM / Juzgados / AP / TS)' },
    { value: 'OTHER', label: 'Other countries' },
  ];

  const legalPoints = [
    { value: 'likelihood_of_confusion', label: 'Likelihood of confusion' },
    { value: 'proof_of_use', label: 'Proof of use' },
    { value: 'reputation', label: 'Reputation' },
    { value: 'similarity', label: 'Similarity' },
    { value: 'bad_faith', label: 'Bad faith' },
    { value: 'relative_grounds', label: 'Relative grounds' },
  ];

  const courts = [
    { value: 'EUIPO Board of Appeal', label: 'EUIPO Board of Appeal' },
    { value: 'General Court', label: 'General Court' },
    { value: 'Audiencia Provincial de Madrid', label: 'Audiencia Provincial de Madrid' },
    { value: 'Juzgado Mercantil de Barcelona', label: 'Juzgado Mercantil de Barcelona' },
  ];

  const toggleSelection = (value: string, current: string[], setter: (val: string[]) => void) => {
    if (current.includes(value)) {
      setter(current.filter(v => v !== value));
    } else {
      setter([...current, value]);
    }
  };

  const handleSearch = () => {
    const params: DartsIPSearchParams = {
      basic_search: {
        keywords: keywords.trim() ? keywords.split(',').map(k => k.trim()) : undefined,
        jurisdiction: selectedJurisdictions.length > 0 ? selectedJurisdictions : undefined,
        date_range: (dateFrom || dateTo) ? {
          from: dateFrom || undefined,
          to: dateTo || undefined,
        } : undefined,
      },
      advanced_search: {
        procedure_type: selectedProcedures.length > 0 ? selectedProcedures : undefined,
        legal_points: selectedLegalPoints.length > 0 ? selectedLegalPoints : undefined,
        courts: selectedCourts.length > 0 ? selectedCourts : undefined,
      },
      pagination: {
        page: 1,
        page_size: 25,
      },
      sorting: {
        field: 'relevance_score',
        order: 'desc',
      },
      response_options: {
        include_relevance_score: true,
        include_legal_points: true,
        include_ai_summary: true,
        include_documents_metadata: true,
        include_statistics: true,
      },
    };

    onSearch(params);
  };

  const clearFilters = () => {
    setKeywords('');
    setSelectedProcedures([]);
    setSelectedJurisdictions([]);
    setSelectedLegalPoints([]);
    setSelectedCourts([]);
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="px-4 pb-3">
      <div className="bg-white border border-border rounded-lg p-3">
        {/* Main Row: Filters on left, Buttons on right */}
        <div className="flex items-start justify-between gap-3">
          {/* Left Side: Basic Filters */}
          <div className="flex-1 space-y-2">
            {/* Procedure Types */}
            <div className="space-y-1">
              <Label className="text-[10px] font-medium text-muted-foreground">Procedure Type:</Label>
              <div className="flex flex-wrap gap-1">
                {procedures.map((p) => (
                  <Badge
                    key={p.value}
                    variant={selectedProcedures.includes(p.value) ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer text-[10px] px-2 py-0.5 h-auto transition-colors",
                      selectedProcedures.includes(p.value)
                        ? "bg-primary hover:bg-primary/90 text-primary-foreground border-0"
                        : "bg-background hover:bg-muted border-input text-foreground"
                    )}
                    onClick={() => toggleSelection(p.value, selectedProcedures, setSelectedProcedures)}
                  >
                    {p.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Jurisdictions */}
            <div className="space-y-1">
              <Label className="text-[10px] font-medium text-muted-foreground">Jurisdiction / Agency:</Label>
              <div className="flex flex-wrap gap-1">
                {jurisdictions.map((j) => (
                  <Badge
                    key={j.value}
                    variant={selectedJurisdictions.includes(j.value) ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer text-[10px] px-2 py-0.5 h-auto transition-colors",
                      selectedJurisdictions.includes(j.value)
                        ? "bg-primary hover:bg-primary/90 text-primary-foreground border-0"
                        : "bg-background hover:bg-muted border-input text-foreground"
                    )}
                    onClick={() => toggleSelection(j.value, selectedJurisdictions, setSelectedJurisdictions)}
                  >
                    {j.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Legal Points */}
            <div className="space-y-1">
              <Label className="text-[10px] font-medium text-muted-foreground">Main Legal Point:</Label>
              <div className="flex flex-wrap gap-1">
                {legalPoints.map((lp) => (
                  <Badge
                    key={lp.value}
                    variant={selectedLegalPoints.includes(lp.value) ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer text-[10px] px-2 py-0.5 h-auto transition-colors",
                      selectedLegalPoints.includes(lp.value)
                        ? "bg-primary hover:bg-primary/90 text-primary-foreground border-0"
                        : "bg-background hover:bg-muted border-input text-foreground"
                    )}
                    onClick={() => toggleSelection(lp.value, selectedLegalPoints, setSelectedLegalPoints)}
                  >
                    {lp.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side: Action Buttons */}
          <div className="flex gap-1.5">
            <Button 
              onClick={handleSearch}
              disabled={isSearching}
              size="sm"
              className="px-2.5 h-8 text-xs"
            >
              <Search className="w-3.5 h-3.5 mr-1.5" />
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={cn(
                "px-2.5 h-8",
                showAdvanced && "bg-muted"
              )}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="space-y-2 pt-2.5 mt-2.5 border-t border-border">
            <div className="flex items-center justify-between">
              <h4 className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
                <SlidersHorizontal className="w-3 h-3" />
                Advanced Filters
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-[10px] h-6 px-2"
              >
                <X className="w-2.5 h-2.5 mr-1" />
                Clear
              </Button>
            </div>

            {/* Keywords */}
            <div className="space-y-1">
              <Label className="text-[10px] font-medium text-muted-foreground">Keywords:</Label>
              <Input
                placeholder="Separated by commas"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="text-xs h-8"
              />
            </div>

            {/* Courts */}
            <div className="space-y-1">
              <Label className="text-[10px] font-medium text-muted-foreground">Courts:</Label>
              <div className="flex flex-wrap gap-1">
                {courts.map((c) => (
                  <Badge
                    key={c.value}
                    variant={selectedCourts.includes(c.value) ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer text-[10px] px-2 py-0.5 h-auto transition-colors",
                      selectedCourts.includes(c.value)
                        ? "bg-primary hover:bg-primary/90 text-primary-foreground border-0"
                        : "bg-background hover:bg-muted border-input text-foreground"
                    )}
                    onClick={() => toggleSelection(c.value, selectedCourts, setSelectedCourts)}
                  >
                    {c.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] font-medium text-muted-foreground">From:</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="text-[10px] h-7"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-medium text-muted-foreground">To:</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="text-[10px] h-7"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchFilters;

